/**
 * usePermissions Hook
 * React hook for checking user permissions in components
 *
 * Usage:
 * const { hasMinimumRole, canAccessAdminTab, canAccessAdminSection } = usePermissions();
 *
 * if (canAccessAdminSection()) {
 *   // Show admin navigation
 * }
 */

import { useAuthStore } from '@/features/auth';
import {
  hasMinimumRole as checkMinimumRole,
  canAccessAdminTab as checkAdminTab,
  canAccessAdminSection as checkAdminSection,
  getAccessibleAdminTabs as getAdminTabs,
  type UserRole,
  type AdminTab
} from '@/utils/permissions';

export function usePermissions() {
  const { user } = useAuthStore();

  return {
    /**
     * Check if user has minimum required role
     * @param requiredRole Minimum role required
     * @returns true if user meets or exceeds the required role level
     */
    hasMinimumRole: (requiredRole: UserRole): boolean => {
      if (!user) return false;
      return checkMinimumRole(user.role as UserRole, requiredRole);
    },

    /**
     * Check if user can access a specific admin tab
     * @param tabId Admin tab identifier
     * @returns true if user has permission to access the tab
     */
    canAccessAdminTab: (tabId: AdminTab): boolean => {
      if (!user) return false;
      return checkAdminTab(user.role as UserRole, tabId);
    },

    /**
     * Check if user can access the admin section at all
     * @returns true if user can access admin section (OPERATOR+)
     */
    canAccessAdminSection: (): boolean => {
      if (!user) return false;
      return checkAdminSection(user.role as UserRole);
    },

    /**
     * Get all admin tabs that the user can access
     * @returns Array of admin tab IDs the user can access
     */
    getAccessibleAdminTabs: (): AdminTab[] => {
      if (!user) return [];
      return getAdminTabs(user.role as UserRole);
    },

    /**
     * Get current user's role
     * @returns Current user's role or null if not authenticated
     */
    getUserRole: (): UserRole | null => {
      return (user?.role as UserRole) || null;
    }
  };
}
